import React from 'react';
import cx from 'classnames';
import { css } from 'emotion';
import {
  PhotoItemValue,
} from '@fulcrumapp/fulcrum-core';
import { flatten } from 'lodash';
import elementResizeDetector from 'element-resize-detector';
import { cssInJS } from '../../utils/styles';
import { BackIcon, ForwardIcon } from '../../icons';
import Tooltip from '../../shared/tooltip';
import { AppContext } from '../../hooks/use-app';
import MediaGalleryView from './media-gallery-view';
import type { DisplayableItem, Metadata } from './display/media-displayer';
import 'react-inner-image-zoom/lib/InnerImageZoom/styles.min.css';

const FPS = 30;
const ESC_KEY = 27;
const PREV_KEY = 37;
const NEXT_KEY = 39;

type Props = {
  className?: string,
  mediaValueItems: Array<DisplayableItem>,
  item: DisplayableItem,
  metadata?: any,
  onPrevious: () => void,
  onNext: () => void,
  onClose: () => void,
  onMediaChange: (item: DisplayableItem) => void,
  index?: number,
  onTimeUpdate?: (time: number) => void,
  initialTime?: number,
  seekTime?: number,
  showLabels?: boolean,
  showText?: boolean,
}

type StateType = {
  imageWidth: number | null,
  imageHeight: number | null,
  recognitionVisible: boolean,
  isZoomable: boolean,
  panoramaEnabled: boolean,
}
export default class MediaContentView extends React.Component<Props, StateType> {
  static readonly contextType = AppContext;
  _sizeDetector: any;
  _viewer: any;
  _image: any;
  _currentMediaUpdate: any;
  _recognition: any;

  constructor(props: Props) {
    super(props);

    this._sizeDetector = elementResizeDetector();

    this.state = {
      imageWidth: null,
      imageHeight: null,
      recognitionVisible: true,
      isZoomable: false,
      panoramaEnabled: this.panoramaViewPreference(),
    };
  }

  componentDidMount() {
    this._sizeDetector.listenTo(this._viewer, this.handleResize);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate() {
    Tooltip.rebuild();
  }

  componentWillUnmount() {
    clearInterval(this._currentMediaUpdate);

    this._sizeDetector.removeListener(this._viewer, this.handleResize);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  panoramaViewPreference() {
    // if there is no current preference saved, default to true, otherwise use localstorage value
    return localStorage.getItem('panoramaViewEnabled') === null ? true : localStorage.getItem('panoramaViewEnabled') === 'true';
  }

  handleKeyDown = (event) => {
    switch (event.keyCode) {
      case PREV_KEY:
        this.props.onPrevious();
        break;
      case NEXT_KEY:
        this.props.onNext();
        break;
      case ESC_KEY:
        this.props.onClose();
        break;
      default:
        break;
    }
  };

  render() {
    // @ts-ignore
    const { styles } = MediaContentView;

    const viewerClass = this.isSingleMedia ? styles.viewerOne : styles.viewer;

    return (
      <div className={cx(this.props.className, styles.container)}>
        <div ref={this.bindViewer} className={viewerClass}>
          <div className={styles.align} />
          {this.renderMedia()}
          {this.renderRecognition()}
          {this.renderPrevious()}
          {this.renderNext()}
        </div>
        {this.renderGallery()}
      </div>
    );
  }

  get isSingleMedia() {
    return !this.props.mediaValueItems || this.props.mediaValueItems.length === 1;
  }

  renderPrevious() {
    if (this.isSingleMedia) {
      return null;
    }

    // @ts-ignore
    const { styles } = MediaContentView;

    return (
      <div
        className={css(styles.prev, this.props.index === 0 && styles.noArrow)}
        onClick={this.props.onPrevious}
      >
        <BackIcon />
      </div>
    );
  }

  renderNext() {
    if (this.isSingleMedia) {
      return null;
    }
    // @ts-ignore
    const { styles } = MediaContentView;

    return (
      <div
        className={css(styles.next, this.props.index === this.props.mediaValueItems.length - 1 && styles.noArrow)}
        onClick={this.props.onNext}
      >
        <ForwardIcon />
      </div>
    );
  }

  renderGallery() {
    if (this.isSingleMedia) {
      return null;
    }
    // @ts-ignore
    const { styles } = MediaContentView;

    return (
      <div className={styles.preview}>
        <MediaGalleryView
          mediaValueItems={this.props.mediaValueItems}
          activeItem={this.props.item}
          onMediaChange={this.props.onMediaChange}
        />
      </div>
    );
  }

  renderMedia() {
    const { item } = this.props;

    if (!this.props.metadata) {
      return null;
    }

    if (!this.props.metadata.uploaded) {
      return this.renderNotUploaded();
    } if (!this.props.metadata.stored) {
      return this.renderNotProcessed();
    }

    const enableRecognition = () => {
      if (this.setState) {
        this.setState({
          recognitionVisible: true,
        });
      }
      // Calling resize immediately gets the wrong position of the image, so do it
      // slightly async so the image is positioned correctly
      window.setTimeout(this.handleResize, 25);
    };

    const disableRecognition = () => {
      if (this.setState) {
        this.setState({
          recognitionVisible: false,
        });
      }
    };

    const handlers = {
      enableRecognition,
      disableRecognition,
      switchPhotoViewer: this.handlePhotoViewSwitch,
      onResize: this.handleResize,
      onPlay: this.handlePlay,
      onPause: this.handlePause,
      onTimeUpdate: this.handleTimeUpdate,
      onSeeked: this.handleSeeked,
      onSeeking: this.handleSeeking,
      onError: this.handleError,
    };

    const metadata: Metadata = {
      isZoomable: this.state.isZoomable,
      ref: this.bindImage,
      panoramaEnabled: this.state.panoramaEnabled,
      initialTime: this.props.initialTime,
      seekTime: this.props.seekTime,
      ...this.props.metadata,
    };

    // Keyed so React always treats a different item's display subtree as distinct, even if the
    // loading-gap (returning null above while metadata is undefined) behavior changes later.
    return (
      <React.Fragment key={item.item.mediaID}>
        {item.displayer.display(metadata, handlers)}
      </React.Fragment>
    );
  }

  renderNotUploaded() {
    // @ts-ignore
    const { styles } = MediaContentView;

    return <span className={styles.notAvailable}>This file has not been uploaded yet.</span>;
  }

  renderNotProcessed() {
    // @ts-ignore
    const { styles } = MediaContentView;

    return <span className={styles.notAvailable}>This media is still processing.</span>;
  }

  renderRecognition() {
    const { metadata } = this.props;

    if (this.state.recognitionVisible && metadata && (this.props.item instanceof PhotoItemValue)) {
      return (
        <div className={styles.recognition} ref={this.bindRecognition}>
          <svg
            className={styles.textBoxContainer}
            width={this.state.imageWidth || 0}
            height={this.state.imageHeight || 0}
          >
            {this.renderPhotoLabels()}
            {this.renderPhotoText()}
          </svg>
        </div>
      );
    }
    return null;
  }

  renderPhotoLabels() {
    const { labels } = this.props.metadata;

    if (!labels || !this.props.showLabels) {
      return null;
    }

    const boxes = flatten(labels.labels.map((label) => label.instances.map((instance) => ({
      name: label.name,
      box: instance.bounding_box,
      coords: {
        x: instance.bounding_box.left * (this.state.imageWidth || 0),
        y: instance.bounding_box.top * (this.state.imageHeight || 0),
        width: instance.bounding_box.width * (this.state.imageWidth || 0),
        height: instance.bounding_box.height * (this.state.imageHeight || 0),
      },
    }))));

    return boxes.map((box, index) => (
      <rect
        // @ts-ignore
        x={box.coords.x}
        // @ts-ignore
        y={box.coords.y}
        // @ts-ignore
        width={box.coords.width}
        // @ts-ignore
        height={box.coords.height}
        key={index}
        className={styles.labelPolygon}
        // @ts-ignore
        data-tip={box.name}
        data-delay-show={100}
      />
    ));
  }

  renderPhotoText() {
    const { text } = this.props.metadata;

    if (!text || !this.props.showText) {
      return null;
    }

    const boxes = flatten(text.text_detections.map((detection) => ({
      text: detection.detected_text,
      box: detection.geometry.bounding_box,
      polygon: detection.geometry.polygon,
      coords: detection.geometry.polygon.map((coord) => `${Math.round(coord.x * (this.state.imageWidth || 0))},${Math.round(coord.y * (this.state.imageHeight || 0))}`).join(' '),
    })));

    return boxes.map((box, index) => (
      <polygon
        key={index}
        // @ts-ignore
        points={box.coords}
        className={styles.textBoxPolygon}
        // @ts-ignore
        data-tip={box.text}
        data-delay-show={100}
      />
    ));
  }

  handlePlay = (event) => {
    clearInterval(this._currentMediaUpdate);

    const element = event.target;

    this._currentMediaUpdate = setInterval(() => {
      this.props.onTimeUpdate?.(element.currentTime);
    }, 1000 / FPS);
  };

  handlePause = (event) => {
    clearInterval(this._currentMediaUpdate);

    this.props.onTimeUpdate?.(event.target.currentTime);
  };

  handleTimeUpdate = (event) => {
    this.props.onTimeUpdate?.(event.target.currentTime);
  };

  handleSeeked = (event) => {
    this.props.onTimeUpdate?.(event.target.currentTime);
  };

  handleSeeking = (event) => {
    this.props.onTimeUpdate?.(event.target.currentTime);
  };

  handleError = (event) => {
    this.props.onTimeUpdate?.(event.target.currentTime);
  };

  handlePhotoViewSwitch = () => {
    this.setState((prevState) => {
      const newSetting = !prevState.panoramaEnabled;
      localStorage.setItem('panoramaViewEnabled', newSetting ? 'true' : 'false');
      return { panoramaEnabled: newSetting };
    });
  };

  maybeEnableZoom(thumbWidth, thumbHeight) {
    const img = new Image();
    img.onload = () => {
      // is the large image bigger than the original?
      const fullsizeIsLarger = (img.width > thumbWidth || img.height > thumbHeight);
      this.setState({
        isZoomable: fullsizeIsLarger,
      });
    };
    img.src = this.props.metadata.original;
  }

  handleResize = () => {
    if (this._image && this._recognition) {
      this._recognition.style.left = `${this._image.offsetLeft}px`;
      this._recognition.style.top = `${this._image.offsetTop}px`;
      this._recognition.style.width = `${this._image.offsetWidth}px`;
      this._recognition.style.height = `${this._image.offsetHeight}px`;

      this.setState({
        imageWidth: this._image.offsetWidth,
        imageHeight: this._image.offsetHeight,
      });
    }
    if (this._image) {
      this.maybeEnableZoom(this._image.offsetWidth, this._image.offsetHeight);
    }
  };

  bindViewer = (node) => {
    this._viewer = node;
  };

  bindImage = (node) => {
    this._image = node;
  };

  bindRecognition = (node) => {
    this._recognition = node;
  };
}

const styles = cssInJS({
  container: {
    backgroundColor: '#fff',
    textAlign: 'center',
    userSelect: 'none',
  },

  viewer: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 120,
    backgroundColor: '#a9a9a9',
    // backgroundColor: '#404040'
    // backgroundColor: '#fff'
  },

  viewerOne: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#a9a9a9',
    // @ts-ignore
    '@phone': {
      position: 'static',
    },
  },

  notAvailable: {
    backgroundColor: '#fff',
    padding: 15,
    borderRadius: 3,
  },

  prev: {
    position: 'absolute',
    left: 0,
    height: 40,
    width: 40,
    top: '50%',
    marginTop: -20,
    textAlign: 'center',
    color: '#fff',
    paddingTop: 10,
    cursor: 'pointer',
    fill: '#fff',
  },

  next: {
    position: 'absolute',
    right: 0,
    height: 40,
    top: '50%',
    marginTop: -20,
    width: 40,
    textAlign: 'center',
    color: '#fff',
    paddingTop: 10,
    cursor: 'pointer',
    fill: '#fff',
  },

  noArrow: {
    visibility: 'hidden',
  },

  textBoxContainer: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    right: 0,
  },

  textBoxPolygon: {
    stroke: 'blue',
    strokeWidth: 1,
    fill: 'transparent',
    strokeLinejoin: 'round',
  },

  labelPolygon: {
    stroke: 'white',
    strokeWidth: 1,
    fill: 'transparent',
    strokeLinejoin: 'round',
  },

  preview: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    right: 0,
    height: 120,
    backgroundColor: '#717171',
  },

  recognition: {
    pointerEvents: 'none',
    position: 'absolute',
  },

  imgContainer: {
    display: 'inline-block',
    maxWidth: '85% !important',
    maxHeight: '85%',
    margin: 'auto auto',
    verticalAlign: 'middle',
    backgroundColor: '#000',
    boxShadow: '0 14px 45px rgba(0,0,0,0.25), 0 10px 18px rgba(0,0,0,0.22)',
    borderRadius: 3,
    objectFit: 'contain',
    position: 'relative',
    // @ts-ignore
    '@phone': {
      maxWidth: '100%',
      maxHeight: '100%',
      borderRadius: 0,
      boxShadow: 'none',
    },
  },
  align: {
    display: 'inline-block',
    width: 0,
    verticalAlign: 'middle',
    height: '100%',
  },
}) as any;
// @ts-ignore
MediaContentView.styles = styles;
